import { useMemo, useState, type RefObject } from 'react'
import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import type { FailoverGroup, Host } from '../../api'
import { t } from '../../i18n'
import { Chip, Icon, Note, OptionList, type Option } from '../../ui'
import { NODES_KEY, fetchNodes } from '../Nodes'
import { WordField } from './confirm'
import { GroupDialog } from './GroupDialog'
import { READY_LOOK, adminErrorText, groupAdminApi, isDanger, readinessLines, readinessOk, readinessWord, unfitReason, useDryRun } from './groupAdmin'
import type { GroupMemberView, Readiness } from './groupTypes'
import { nodePath } from './api'
import { tn } from './nodeSwitch'
import { WarningNotes } from './Warnings'

/*
 * The composition of a node group on its page (GROUP-UI-3; owner 01.11):
 * - Add node (NG-14 A): one option list of the nodes outside the group; the ones that cannot
 *   join stay visible, disabled, with the reason under them (direct egress, another group, no
 *   host agent, revoked, integrations — owner B-3, B-4). One node per add. The center's dry run
 *   adds its warnings first (V3: members on different sites — warn and allow).
 *   DS: NG-14 — the list may pass 6 options (TrackSelection says a select then); kept as a list
 *   by the owner so the reasons stay readable.
 * - Remove from group (NG-15): only a DRAINED or MAINTENANCE member (empty); a plain button.
 * - Delete group (owner B-7): only an empty group; level 3 with the name typed, danger.
 * - Readiness (NG-16 C): only what needs work, as notes; what is OK folds into one line.
 */

/* ── add ─────────────────────────────────────────────────────────────────── */

export function AddNodeDialog({ group, groups, hostOf, prov, rampMin, dry, trigger, onClose, onDone }: {
  group: FailoverGroup
  groups: FailoverGroup[]
  hostOf: (node: string) => Host | undefined
  prov: boolean
  rampMin?: number
  /** the center has GROUP-DEFAULT: dry runs are safe */
  dry: boolean
  trigger: RefObject<HTMLElement | null>
  onClose: () => void
  onDone: (text: string) => Promise<void>
}) {
  const nodes = useQuery({ queryKey: NODES_KEY, queryFn: fetchNodes })
  const rows = useMemo(() => {
    const out = (nodes.data ?? [])
      .filter((n) => !(n.failover?.mode === 'group' && n.failover.group === group.id))
      .map((n) => ({ n, why: unfitReason(n, group, { host: hostOf(n.id), prov, groups }) }))
    // the ones that can join first, then by name; revoked last
    return out.sort((a, b) => Number(!!a.why) - Number(!!b.why) || Number(a.n.status === 'revoked') - Number(b.n.status === 'revoked') || a.n.name.localeCompare(b.n.name))
  }, [nodes.data, group, groups, hostOf, prov])
  const [pick, setPick] = useState('')
  const chosen = rows.find((r) => r.n.id === pick && !r.why)?.n
  const dr = useDryRun(chosen ? { method: 'PUT', path: `/api/admin/nodes/${encodeURIComponent(chosen.id)}/failover`, body: { mode: 'group', group: group.id } } : null, dry)
  const opts: Option<string>[] = rows.map(({ n, why }) => ({
    value: n.id,
    label: <span className="sx-mono">{n.name}</span>,
    right: n.region || '—',
    why,
    testId: `group-add-${n.id}`,
  }))
  const fit = rows.filter((r) => !r.why).length
  return (
    <GroupDialog
      testId="group-add"
      title={tn('gx.add.title', { group: group.name })}
      action={t('gx.add.btn')}
      ready={!!chosen && !dr.pending}
      danger={isDanger(dr.warnings)}
      returnFocus={trigger}
      onClose={onClose}
      errorText={(e) => adminErrorText(e, { name: chosen?.name })}
      submit={async (totp) => {
        await groupAdminApi.join(chosen!.id, group.id, totp)
        await onDone(t('gx.add.done', { name: chosen!.name, group: group.name }))
      }}
    >
      <WarningNotes warnings={dr.warnings} ctx={{ name: chosen?.name, group: group.name }} testId="group-add-warnings" />
      {nodes.isPending ? (
        <span className="sx-cap">…</span>
      ) : rows.length === 0 || fit === 0 ? (
        <Note data-testid="group-add-none">{t('gx.add.none')}</Note>
      ) : null}
      {rows.length > 0 && <OptionList value={pick} options={opts} onChange={setPick} label={t('gx.add.label')} />}
      {chosen && (
        <ul data-testid="group-add-facts">
          <li>{rampMin ? tn('gx.add.ramp', { name: chosen.name, min: rampMin }) : tn('gx.add.rampManual', { name: chosen.name })}</li>
          <li>{t('gx.add.cert')}</li>
        </ul>
      )}
    </GroupDialog>
  )
}

/* ── remove ──────────────────────────────────────────────────────────────── */

/** Remove works on an empty member only: DRAINED or MAINTENANCE (NG-15). */
export const canRemove = (m: Pick<GroupMemberView, 'state'>) => m.state === 'drained' || m.state === 'maintenance'

export function RemoveDialog({ m, group, dry, trigger, onClose, onDone }: {
  m: GroupMemberView
  group: FailoverGroup
  dry: boolean
  trigger: RefObject<HTMLElement | null>
  onClose: () => void
  onDone: (text: string) => Promise<void>
}) {
  const dr = useDryRun({ method: 'PUT', path: `/api/admin/nodes/${encodeURIComponent(m.node)}/failover`, body: { mode: 'none' } }, dry)
  return (
    <GroupDialog
      testId="member-remove"
      title={tn('gx.remove.title', { name: m.name, group: group.name })}
      facts={[tn('gx.remove.empty', { name: m.name, n: m.connections }), t('gx.remove.name'), t('gx.remove.roles')]}
      action={t('gx.remove.btn')}
      ready={!dr.pending}
      danger={isDanger(dr.warnings)}
      returnFocus={trigger}
      onClose={onClose}
      errorText={(e) => adminErrorText(e, { name: m.name })}
      submit={async (totp) => {
        await groupAdminApi.leave(m.node, totp)
        await onDone(t('gx.remove.done', { name: m.name, group: group.name }))
      }}
    >
      <WarningNotes warnings={dr.warnings} ctx={{ name: m.name, group: group.name }} testId="member-remove-warnings" />
    </GroupDialog>
  )
}

/* ── delete ──────────────────────────────────────────────────────────────── */

export function DeleteGroupDialog({ group, isDefault, trigger, onClose, onDone }: { group: FailoverGroup; isDefault: boolean; trigger: RefObject<HTMLElement | null>; onClose: () => void; onDone: () => void }) {
  const [word, setWord] = useState('')
  return (
    <GroupDialog
      testId="group-delete"
      title={tn('gx.delete.title', { name: group.name })}
      facts={[t('gx.delete.dns', { fqdn: group.fqdn ?? group.name }), t('gx.delete.cabinet'), ...(isDefault ? [t('gx.delete.default')] : [])]}
      action={t('gx.delete.btn')}
      danger
      ready={word.trim() === group.name}
      returnFocus={trigger}
      onClose={onClose}
      errorText={(e) => adminErrorText(e, { name: group.name })}
      submit={async (totp) => {
        await groupAdminApi.remove(group.id, totp)
        onDone()
      }}
    >
      <WordField word={group.name} value={word} onChange={setWord} testId="group-delete-word" />
    </GroupDialog>
  )
}

/* ── readiness ───────────────────────────────────────────────────────────── */

export function ReadinessPanel({ r, fqdn, group, isDefault, nameOf }: { r: Readiness; fqdn: string; group: string; isDefault: boolean; nameOf: (id?: string) => string }) {
  const lines = readinessLines(r, { fqdn, group })
  const word = readinessWord(lines, r.state)
  const look = READY_LOOK[word]
  const bad = lines.filter((l) => l.state !== 'pending')
  const waiting = lines.filter((l) => l.state === 'pending')
  const ok = readinessOk(r.items, isDefault)
  return (
    <section className="sx-panel flex flex-col gap-3" data-testid="group-readiness" data-state={word}>
      <div className="flex items-center justify-between gap-3">
        <span className="sx-label">{t('gx.ready.title')}</span>
        <Chip state={look.state} still data-testid="group-readiness-chip">{t(look.word)}</Chip>
      </div>
      {bad.map((x, i) => (
        <Note key={`${x.key}-${x.node ?? ''}-${i}`} state={x.state === 'fail' ? 'fail' : 'warn'} data-testid={`group-readiness-${x.key}${x.node ? `-${x.node}` : ''}`}>
          <b className="font-medium text-text">{tn(...x.title)}</b>
          {x.body && <> {tn(...x.body)}</>}
          {x.extra && <> {x.extra}</>}
          {x.node && (
            <>
              {' '}
              <Link to={nodePath(x.node)} className="text-accent hover:underline">{tn('gx.ready.open', { name: nameOf(x.node) })}</Link>
            </>
          )}
        </Note>
      ))}
      {(ok.length > 0 || waiting.length > 0) && (
        <p className="m-0 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted" data-testid="group-readiness-ok">
          {ok.length > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Icon name="state-live" size={12} className="text-live" />
              {t(ok.length === 1 ? 'gx.ready.okLine.one' : 'gx.ready.okLine.many', { n: ok.length, list: ok.join(', ') })}
            </span>
          )}
          {waiting.map((x, i) => (
            <span key={`w-${i}`} className="inline-flex items-center gap-1.5" data-testid={`group-readiness-wait-${x.key}`}>
              <Icon name="state-start" size={12} className="text-start" />
              {tn(...x.title)}
            </span>
          ))}
        </p>
      )}
    </section>
  )
}
